import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import { BASE_URL } from "../config";
import DeviceKioskHeader from "./DeviceKioskHeader";
import { getTodayDateOnly } from "./deviceKiosk";
import {
  clearManageContext,
  loadManageContext,
  loadManageSelectedLocations,
  saveManageContext,
  saveManageSelectedLocations,
} from "./manageContext";
import { clearManageSession, manageFetch } from "./manageSession";

export default function DeviceAssign() {
  usePageMeta("Assign Location");
  const navigate = useNavigate();
  const location = useLocation();
  const [now, setNow] = useState(new Date());
  const [query, setQuery] = useState("");
  const [clients, setClients] = useState(() =>
    Array.isArray(location.state?.clients) ? location.state.clients : [],
  );
  const [selectedClient, setSelectedClient] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const manageContext = useMemo(() => loadManageContext(location), [location]);
  const debounceRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const initialEmail = manageContext?.email || location.state?.email;
    if (!initialEmail) {
      navigate("/device/signin", { replace: true });
    }
  }, [location.state?.email, manageContext?.email, navigate]);

  const loadClients = async (search = "") => {
    setLoadingClients(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      const response = await manageFetch(
        `${BASE_URL}/api/jobs/manage/device-clients?${params.toString()}`,
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || "Failed to load clients");
      }
      const rows = Array.isArray(payload?.data) ? payload.data : [];
      setClients(rows);
      if (!selectedClient && rows.length === 1) {
        setSelectedClient(rows[0]);
      }
    } catch (err) {
      setError(err?.message || "Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  };

  useEffect(() => {
    if (!clients.length) {
      loadClients();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadClients(query.trim());
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const clientId = Number(selectedClient?.id);
    if (!Number.isFinite(clientId) || clientId <= 0) {
      setLocations([]);
      setSelectedIds(new Set());
      return;
    }

    const loadClientLocations = async () => {
      setLoadingLocations(true);
      setError("");
      try {
        const [locationsRes, deviceRes] = await Promise.all([
          manageFetch(`${BASE_URL}/api/jobs/manage/device-locations?clientId=${clientId}`),
          manageFetch(
            `${BASE_URL}/api/jobs/manage/device?clientId=${clientId}&shiftDate=${getTodayDateOnly()}`,
          ),
        ]);
        const locationsPayload = await locationsRes.json().catch(() => []);
        const devicePayload = await deviceRes.json().catch(() => ({}));
        if (!locationsRes.ok) {
          throw new Error(locationsPayload?.message || "Failed to load locations");
        }
        if (!deviceRes.ok) {
          throw new Error(devicePayload?.message || "Failed to load assignment");
        }
        const rows = Array.isArray(locationsPayload) ? locationsPayload : [];
        const selected = new Set(
          loadManageSelectedLocations({
            clientId,
            clientDeviceId: manageContext?.clientDeviceId || devicePayload?.data?.clientDeviceId || null,
          }),
        );
        setLocations(rows);
        setSelectedIds(selected);
      } catch (err) {
        setError(err?.message || "Failed to load locations");
      } finally {
        setLoadingLocations(false);
      }
    };

    loadClientLocations();
  }, [selectedClient?.id]);

  const toggleLocation = (id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleAssign = async () => {
    const clientId = Number(selectedClient?.id);
    if (!Number.isFinite(clientId) || clientId <= 0) {
      setError("Please select a client.");
      return;
    }
    if (!selectedIds.size) {
      setError("Please select at least one location.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const date = getTodayDateOnly();
      const response = await manageFetch(`${BASE_URL}/api/jobs/manage/device`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId,
          shiftDate: date,
          from: date,
          selectedLocationIds: [...selectedIds],
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || "Failed to assign");
      }

      const selectedLocationNames = locations
        .filter((row) => selectedIds.has(String(row.id)))
        .map((row) => row.label || row.name)
        .filter(Boolean);
      const nextContext = {
        ...manageContext,
        clientId,
        clientName: selectedClient?.name || manageContext?.clientName || "Client",
        locationName: selectedLocationNames.length === 1 ? selectedLocationNames[0] : "",
        shiftDate: date,
        startDate: date,
        endDate: date,
        clientDeviceId: payload?.data?.clientDeviceId || manageContext?.clientDeviceId || null,
        selectedLocationIds: [...selectedIds],
      };
      saveManageContext(nextContext);
      saveManageSelectedLocations({
        clientId,
        clientDeviceId: nextContext.clientDeviceId,
        selectedLocationIds: [...selectedIds],
      });

      const params = new URLSearchParams({
        clientId: String(clientId),
        from: date,
        client: selectedClient?.name || "Client",
      });
      if (selectedLocationNames.length === 1) {
        params.set("location", selectedLocationNames[0]);
      }
      navigate(`/checkin?${params.toString()}`, { replace: true });
    } catch (err) {
      setError(err?.message || "Failed to assign");
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = () => {
    clearManageSession();
    clearManageContext();
    navigate("/device/signin", { replace: true });
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <DeviceKioskHeader now={now} />
      <main className="flex-1 px-4">
        <h1 className="mt-6 text-center text-2xl text-gray-700">Assign Location</h1>
        <p className="mx-auto mt-4 max-w-md text-xs text-gray-600 text-center">
          Locations selected here will be shown in the time tracker. Only shifts for selected locations
          can be checked in to with this device.
        </p>

        <div className="mx-auto mt-6 w-full max-w-md">
          <label className="block text-xs text-gray-500 mb-1">Client*</label>
          <div className="border-b border-gray-300">
            <div className="flex items-center gap-3">
              <input
                value={selectedClient ? selectedClient.name : query}
                onChange={(e) => {
                  setSelectedClient(null);
                  setQuery(e.target.value);
                }}
                placeholder="Search client"
                className="w-full bg-transparent py-2 text-sm outline-none focus:border-gray-500"
              />
              {selectedClient ? (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedClient(null);
                    setQuery("");
                    setLocations([]);
                    setSelectedIds(new Set());
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  x
                </button>
              ) : null}
            </div>
          </div>

          {!selectedClient ? (
            <div className="max-h-52 overflow-y-auto border-b border-gray-300 bg-white">
              {loadingClients ? <div className="p-2 text-xs text-gray-500">Loading clients...</div> : null}
              {!loadingClients &&
                clients.map((client) => (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => {
                      setSelectedClient(client);
                      setQuery("");
                    }}
                    className="block w-full border-t border-gray-100 px-2 py-2 text-left text-sm hover:bg-gray-100"
                  >
                    {client.name}
                  </button>
                ))}
            </div>
          ) : null}
        </div>

        <div className="mx-auto mt-6 w-full max-w-md">
          <label className="block text-xs text-gray-500 mb-1">Locations*</label>
          <div className="mt-2 space-y-2 max-h-64 overflow-y-auto pr-2">
            {loadingLocations ? <div className="text-xs text-gray-500">Loading locations...</div> : null}
            {!loadingLocations &&
              locations.map((loc) => (
                <label key={loc.id} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(String(loc.id))}
                    onChange={() => toggleLocation(loc.id)}
                    className="h-3.5 w-3.5 accent-[#DC1F00]"
                  />
                  <span>{loc.label || loc.name}</span>
                </label>
              ))}
          </div>
        </div>

        {error ? <div className="mx-auto mt-3 max-w-md text-sm text-red-600">{error}</div> : null}
      </main>
      <footer className="px-4 pb-4">
        <button
          type="button"
          onClick={handleAssign}
          disabled={saving}
          className="w-full bg-[#DC1F00] text-white py-2 text-sm font-medium uppercase hover:bg-[#b91900] transition-colors disabled:opacity-60"
        >
          {saving ? "Assigning..." : "Assign"}
        </button>
        <button
          type="button"
          onClick={handleSignOut}
          className="w-full mt-2 text-center text-sm font-medium uppercase text-gray-700 hover:text-black transition-colors"
        >
          Sign Out
        </button>
      </footer>
    </div>
  );
}
